import { FormEvent, useState } from 'react';
import { apiClient } from '@edgerunner/shared';

type FeedbackKind = 'bug' | 'idea' | 'usability' | 'other';

export function ProductFeedbackModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [kind, setKind] = useState<FeedbackKind>('bug');
  const [message, setMessage] = useState('');
  const [isBlocking, setIsBlocking] = useState(false);
  const [allowFollowUp, setAllowFollowUp] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [referenceId, setReferenceId] = useState('');

  if (!isOpen) return null;

  const close = () => {
    setMessage('');
    setError('');
    setReferenceId('');
    setIsBlocking(false);
    onClose();
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const cleanMessage = message.trim();
    if (cleanMessage.length < 3) {
      setError('Please add a little more detail.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const result = await apiClient.feedback.create({
        kind,
        message: cleanMessage,
        is_blocking: isBlocking,
        allow_follow_up: allowFollowUp,
        surface: 'operator',
        route: window.location.pathname,
        context: {},
      });
      setReferenceId(result.id);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to send feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-neutral-900/70 flex items-center justify-center z-[5000]" role="dialog" aria-modal="true" aria-label="Send feedback">
      <div className="bg-neutral-700 border border-neutral-400 rounded-medium shadow-lg w-[min(560px,calc(100vw-32px))] max-h-[86vh] overflow-y-auto">
        <div className="flex items-center justify-between px-md py-sm border-b border-neutral-400">
          <h2 className="text-md font-semibold text-neutral-100 m-0">Send feedback</h2>
          <button className="bg-transparent border-0 text-neutral-200 hover:text-red-400 cursor-pointer" onClick={close} aria-label="Close feedback">
            <i className="fas fa-times" />
          </button>
        </div>
        {referenceId ? (
          <div className="p-md">
            <p className="text-neutral-100">Thanks — your feedback has been received.</p>
            <p className="mt-sm text-sm text-neutral-300">Reference: {referenceId}</p>
            <button className="mt-md bg-red-700 hover:bg-red-600 text-neutral-100 px-md py-xs rounded-small border-0 cursor-pointer" onClick={close}>Done</button>
          </div>
        ) : (
          <form className="p-md flex flex-col gap-md" onSubmit={submit}>
            <p className="text-sm text-neutral-300 m-0">Tell us what happened or what would make EdgeRunner better. Conversations and uploaded files are not included.</p>
            <label htmlFor="feedback-kind" className="flex flex-col gap-xs text-sm text-neutral-100">
              Feedback type
              <select id="feedback-kind" className="bg-neutral-800 border border-neutral-500 rounded-small px-sm py-xs text-neutral-100" value={kind} onChange={(event) => setKind(event.target.value as FeedbackKind)}>
                <option value="bug">Bug</option>
                <option value="idea">Idea</option>
                <option value="usability">Usability issue</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label htmlFor="feedback-message" className="flex flex-col gap-xs text-sm text-neutral-100">
              What would you like us to know?
              <textarea id="feedback-message" className="bg-neutral-800 border border-neutral-500 rounded-small px-sm py-xs text-neutral-100 resize-y" value={message} onChange={(event) => setMessage(event.target.value)} maxLength={4000} rows={6} required />
            </label>
            {error && <div className="text-sm text-red-300" role="alert">{error}</div>}
            <label htmlFor="feedback-blocking" className="flex items-center gap-xs text-sm text-neutral-100"><input id="feedback-blocking" type="checkbox" checked={isBlocking} onChange={(event) => setIsBlocking(event.target.checked)} />This is blocking me</label>
            <label htmlFor="feedback-follow-up" className="flex items-center gap-xs text-sm text-neutral-100"><input id="feedback-follow-up" type="checkbox" checked={allowFollowUp} onChange={(event) => setAllowFollowUp(event.target.checked)} />You may contact me about this feedback</label>
            <div className="flex justify-end gap-sm">
              <button type="button" className="bg-neutral-500 hover:bg-neutral-400 text-neutral-100 px-md py-xs rounded-small border-0 cursor-pointer" onClick={close}>Cancel</button>
              <button type="submit" className="bg-red-700 hover:bg-red-600 text-neutral-100 px-md py-xs rounded-small border-0 cursor-pointer disabled:opacity-50" disabled={submitting}>{submitting ? 'Sending…' : 'Send feedback'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
